export type SampleVersion = "original" | "processed";

/** Map playback to the same words, including when the effect slows the voice. */
export function sampleSwitchTime(time: number, fromSpeed: number, toSpeed: number, duration: number): number {
  const mapped = Math.max(0, time * fromSpeed / toSpeed);
  return Number.isFinite(duration) && duration > 0 ? Math.min(mapped, Math.max(0, duration - 0.05)) : mapped;
}

type Voice = { source: AudioBufferSourceNode; gain: GainNode };

/** Decoded samples share one audio clock, so switching never waits for a media seek. */
export class SamplePlayback<Version extends string = SampleVersion> {
  version: Version;
  onended: () => void = () => {};
  private offset = 0;
  private startedAt = 0;
  private voice: Voice | null = null;
  private voices = new Set<Voice>();
  private context: AudioContext;
  private buffers: Record<Version, AudioBuffer>;
  private speeds: Record<Version, number>;
  private restartOnSwitch: boolean;

  constructor(
    context: AudioContext,
    buffers: Record<Version, AudioBuffer>,
    speeds: Record<Version, number>,
    restartOnSwitch = false,
  ) {
    this.context = context;
    const versions = Object.keys(buffers) as Version[];
    if (!versions.length) throw new Error("At least one sample is required");
    this.version = versions.find(version => version === "processed") ?? versions[0]!;
    this.buffers = buffers;
    this.speeds = speeds;
    this.restartOnSwitch = restartOnSwitch;
  }

  get playing(): boolean { return this.voice !== null; }
  get duration(): number { return this.buffers[this.version].duration; }
  get position(): number {
    return Math.min(this.duration, this.offset + (this.playing ? this.context.currentTime - this.startedAt : 0));
  }

  play(): void {
    if (this.playing) return;
    this.startAt(this.position >= this.duration ? 0 : this.position);
  }

  pause(): void {
    this.offset = this.position;
    this.voice = null;
    for (const voice of this.voices) {
      voice.source.onended = null;
      voice.source.stop();
      voice.source.disconnect();
      voice.gain.disconnect();
    }
    this.voices.clear();
  }

  switchTo(next: Version): void {
    if (next === this.version) return;
    const position = this.restartOnSwitch || this.position >= this.duration ? 0 : sampleSwitchTime(
      this.position, this.speeds[this.version], this.speeds[next], this.buffers[next].duration,
    );
    this.version = next;
    if (this.playing) this.startAt(position);
    else this.offset = position;
  }

  seek(position: number): void {
    this.offset = Math.max(0, Math.min(position, this.duration));
    if (this.playing) this.startAt(this.offset);
  }

  private startAt(position: number): void {
    const now = this.context.currentTime;
    const previous = this.voice;
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    const voice = { source, gain };
    source.buffer = this.buffers[this.version];
    source.connect(gain);
    gain.connect(this.context.destination);
    // Five milliseconds smooths the waveform boundary without an audible pause.
    gain.gain.setValueAtTime(previous ? 0 : 1, now);
    if (previous) {
      gain.gain.linearRampToValueAtTime(1, now + 0.005);
      previous.gain.gain.cancelScheduledValues(now);
      previous.gain.gain.setValueAtTime(previous.gain.gain.value, now);
      previous.gain.gain.linearRampToValueAtTime(0, now + 0.005);
      previous.source.stop(now + 0.005);
    }
    this.voice = voice;
    this.voices.add(voice);
    this.offset = position;
    this.startedAt = now;
    source.onended = () => {
      source.disconnect();
      gain.disconnect();
      this.voices.delete(voice);
      if (this.voice !== voice) return;
      this.voice = null;
      this.offset = this.duration;
      this.onended();
    };
    source.start(now, position);
  }
}
